import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  getChannels,
  getHealth,
  getSessions,
} from '../api.js';


const CONNECTIONS = [
  {
    key: 'telegram_personal',
    title: 'Telegram — личный аккаунт',
    icon: 'telegram-personal.svg',
    source: 'session',
    match: (item) => item.messenger_type === 'telegram',
  },
  {
    key: 'telegram_bot',
    title: 'Telegram — бот',
    icon: 'telegram-bot.svg',
    source: 'channel',
    match: (item) => item.channel_type === 'telegram_bot',
  },
  {
    key: 'max_personal',
    title: 'MAX — личный аккаунт',
    icon: 'max-personal.svg',
    source: 'session',
    match: (item) => item.messenger_type === 'max',
  },
  {
    key: 'max_bot',
    title: 'MAX — бот',
    icon: 'max-bot.svg',
    source: 'channel',
    match: (item) => item.channel_type === 'max_bot',
  },
  {
    key: 'whatsapp_personal',
    title: 'WhatsApp — личный аккаунт',
    icon: 'whatsapp-personal.svg',
    source: 'session',
    match: (item) => item.messenger_type === 'whatsapp',
  },
];


const PERSONAL_TYPES = new Set([
  'telegram_personal',
  'max_personal',
  'whatsapp_personal',
]);


function normalized(value) {
  return String(value || '')
    .trim()
    .toLowerCase();
}


function sessionState(session) {
  if (session?.auth_request) {
    return {
      kind: 'auth_required',
      tone: 'warning',
      label: 'Нужен вход',
    };
  }

  const state = normalized(
    session?.worker_status
    || session?.status,
  );

  if (
    state === 'connected'
    || state === 'online'
    || state === 'active'
  ) {
    return {
      kind: 'connected',
      tone: 'success',
      label: 'Подключено',
    };
  }

  if (
    state === 'starting'
    || state === 'connecting'
    || state === 'reconnecting'
  ) {
    return {
      kind: 'connecting',
      tone: 'warning',
      label: 'Подключается',
    };
  }

  if (
    state === 'error'
    || state === 'failed'
    || state === 'banned'
  ) {
    return {
      kind: 'error',
      tone: 'danger',
      label: 'Ошибка',
    };
  }

  if (
    state === 'offline'
    || state === 'disconnected'
  ) {
    return {
      kind: 'offline',
      tone: 'danger',
      label: 'Отключено',
    };
  }

  return {
    kind: 'neutral',
    tone: 'neutral',
    label: 'Не подключено',
  };
}


function connectedSummary(count, total) {
  if (total <= 1) {
    return null;
  }

  if (count === 1) {
    return `1 из ${total} подключён`;
  }

  if (count >= 2 && count <= 4) {
    return `${count} из ${total} подключены`;
  }

  return `${count} из ${total} подключено`;
}


function channelState(channel) {
  return channel?.is_active
    ? {
        tone: 'success',
        label: 'Подключено',
      }
    : {
        tone: 'neutral',
        label: 'Отключено',
      };
}


function identity(item, source) {
  if (!item) {
    return 'Не настроено';
  }

  if (source === 'session') {
    return (
      item.display_name
      || item.username
      || item.phone_number
      || 'Аккаунт подключён'
    );
  }

  return (
    item.name
    || item.username
    || 'Канал подключён'
  );
}


function buildConnection(
  definition,
  sessions,
  channels,
) {
  const pool = (
    definition.source === 'session'
      ? sessions
      : channels
  );

  const matches = pool.filter(
    definition.match,
  );

  if (!matches.length) {
    return {
      ...definition,
      count: 0,
      tone: 'neutral',
      status: 'Не подключено',
      identity: 'Не настроено',
      accounts: [],
    };
  }

  const states = matches.map(
    (item) => (
      definition.source === 'session'
        ? sessionState(item)
        : channelState(item)
    ),
  );

  const accounts = (
    definition.source === 'session'
      ? matches.map((item, index) => {
          const state = sessionState(item);

          return {
            key: (
              item.id
              || item.session_id
              || item.phone_number
              || item.username
              || `${definition.key}-${index}`
            ),
            label: identity(
              item,
              definition.source,
            ),
            tone: state.tone,
            status: state.label,
          };
        })
      : []
  );

  let preferred;
  let status;
  let connectionIdentity;

  if (definition.source === 'session') {
    const counts = states.reduce(
      (result, item) => {
        result[item.kind] = (
          result[item.kind] || 0
        ) + 1;

        return result;
      },
      {},
    );

    const connectedCount = (
      counts.connected || 0
    );

    if (counts.error) {
      preferred = {
        tone: 'danger',
        label: 'Ошибка',
      };
    } else if (counts.auth_required) {
      preferred = {
        tone: 'warning',
        label: 'Требуется вход',
      };
    } else if (counts.connecting) {
      preferred = {
        tone: 'warning',
        label: 'Подключается',
      };
    } else if (
      connectedCount === matches.length
    ) {
      preferred = {
        tone: 'success',
        label: 'Подключено',
      };
    } else if (
      (counts.offline || 0)
      === matches.length
    ) {
      preferred = {
        tone: 'danger',
        label: 'Отключено',
      };
    } else if (
      (counts.neutral || 0)
      === matches.length
    ) {
      preferred = {
        tone: 'neutral',
        label: 'Не подключено',
      };
    } else {
      preferred = {
        tone: 'warning',
        label: 'Требуется внимание',
      };
    }

    status = preferred.label;

    connectionIdentity = (
      matches.length > 1
        ? connectedSummary(
            connectedCount,
            matches.length,
          )
        : identity(
            matches[0],
            definition.source,
          )
    );
  } else {
    preferred = (
      states.find(
        (item) => item.tone === 'success',
      )
      || states.find(
        (item) => item.tone === 'warning',
      )
      || states.find(
        (item) => item.tone === 'danger',
      )
      || states[0]
    );

    status = (
      matches.length > 1
        ? `${matches.length} подключения`
        : preferred.label
    );

    connectionIdentity = identity(
      matches[0],
      definition.source,
    );
  }

  return {
    ...definition,
    count: matches.length,
    tone: preferred.tone,
    status,
    identity: connectionIdentity,
    accounts,
  };
}


function healthTone(value) {
  const state = normalized(
    typeof value === 'string'
      ? value
      : value?.status,
  );

  if (
    state === 'ok'
    || state === 'healthy'
    || state === 'ready'
    || state === 'running'
    || state === 'connected'
  ) {
    return 'success';
  }

  if (
    state === 'warning'
    || state === 'warn'
    || state === 'degraded'
  ) {
    return 'warning';
  }

  if (
    state === 'error'
    || state === 'failed'
    || state === 'down'
    || state === 'unreachable'
  ) {
    return 'danger';
  }

  return 'neutral';
}


function healthLabel(value) {
  const state = normalized(
    typeof value === 'string'
      ? value
      : value?.status,
  );

  const labels = {
    ok: 'Работает',
    healthy: 'Работает',
    ready: 'Работает',
    running: 'Работает',
    connected: 'Работает',
    warning: 'Внимание',
    warn: 'Внимание',
    degraded: 'Ограниченно',
    error: 'Ошибка',
    failed: 'Ошибка',
    down: 'Недоступно',
    unreachable: 'Недоступно',
  };

  return labels[state] || 'Нет данных';
}


function serviceName(value) {
  const names = {
    postgres: 'База данных',
    database: 'База данных',
    redis: 'Очереди',
    minio: 'Файлы',
    storage: 'Хранилище',
    worker: 'Обработчик сообщений',
    bitrix: 'Bitrix24',
  };

  return (
    names[value]
    || String(value || '')
      .replaceAll('_', ' ')
  );
}


function MessengerIcon({
  file,
  title,
}) {
  return (
    <img
      className="sv-messenger-icon"
      src={`/brand/messengers/${file}`}
      alt={title}
    />
  );
}


export default function Dashboard() {
  const [channels, setChannels] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  const [
    expandedConnections,
    setExpandedConnections,
  ] = useState({});

  const load = useCallback(async () => {
    setLoading(true);

    const results = await Promise.allSettled([
      getChannels(),
      getSessions(),
      getHealth(),
    ]);

    if (results[0].status === 'fulfilled') {
      setChannels(
        Array.isArray(results[0].value)
          ? results[0].value
          : [],
      );
    }

    if (results[1].status === 'fulfilled') {
      setSessions(
        Array.isArray(results[1].value)
          ? results[1].value
          : [],
      );
    }

    if (results[2].status === 'fulfilled') {
      setHealth(
        results[2].value || null,
      );
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const managedChannels = useMemo(
    () => channels.filter(
      (channel) => (
        !PERSONAL_TYPES.has(
          channel.channel_type,
        )
      ),
    ),
    [channels],
  );

  const connections = useMemo(
    () => CONNECTIONS.map(
      (definition) => buildConnection(
        definition,
        sessions,
        managedChannels,
      ),
    ),
    [
      sessions,
      managedChannels,
    ],
  );

  const active = connections.filter(
    (item) => item.tone === 'success',
  ).length;

  const attention = connections.filter(
    (item) => (
      item.tone === 'warning'
      || item.tone === 'danger'
    ),
  ).length;

  const configured = connections.filter(
    (item) => item.count > 0,
  ).length;

  const components = Object.entries(
    health?.components || {},
  );

  const systemOkay = (
    normalized(health?.status) === 'ok'
  );

  const toggleConnection = (key) => {
    setExpandedConnections(
      (current) => ({
        ...current,
        [key]: !current[key],
      }),
    );
  };

  const providerCounts = {
    Telegram: connections
      .filter(
        (item) => item.key.startsWith('telegram_'),
      )
      .reduce(
        (sum, item) => sum + item.count,
        0,
      ),

    MAX: connections
      .filter(
        (item) => item.key.startsWith('max_'),
      )
      .reduce(
        (sum, item) => sum + item.count,
        0,
      ),

    WhatsApp: connections
      .filter(
        (item) => item.key.startsWith('whatsapp_'),
      )
      .reduce(
        (sum, item) => sum + item.count,
        0,
      ),
  };

  return (
    <div className="sv-overview">
      <header className="sv-overview-header">
        <h1>Обзор</h1>

        <div className="sv-overview-actions">
          <span
            className={
              `sv-global-state ${
                attention === 0
                  ? 'success'
                  : 'warning'
              }`
            }
          >
            <i />

            {attention === 0
              ? 'Все каналы работают'
              : 'Требуется внимание'}
          </span>

          <button
            type="button"
            className="sv-refresh"
            disabled={loading}
            onClick={load}
          >
            {loading
              ? 'Обновление…'
              : 'Обновить'}
          </button>
        </div>
      </header>

      <section className="sv-panel">
        <div className="sv-panel-head">
          <h2>Состояние подключений</h2>

          <span>
            {active} из {connections.length}
          </span>
        </div>

        <div className="sv-connections-grid">
          {connections.map((item) => (
            <article
              className="sv-connection-card"
              key={item.key}
            >
              <div className="sv-connection-top">
                <MessengerIcon
                  file={item.icon}
                  title={item.title}
                />

                <span
                  className={
                    `sv-status ${item.tone}`
                  }
                >
                  <i />
                  {item.status}
                </span>
              </div>

              <strong className="sv-connection-title">
                {item.title}
              </strong>

              <div className="sv-connection-account">
                {item.identity}
              </div>

              {item.source === 'session' && item.count > 0 && (
                <>
                  <button
                    type="button"
                    className="sv-account-toggle"
                    onClick={() => toggleConnection(item.key)}
                    aria-expanded={
                      Boolean(
                        expandedConnections[item.key],
                      )
                    }
                  >
                    <span>
                      {item.count === 1
                        ? 'Показать аккаунт'
                        : `Аккаунты · ${item.count}`}
                    </span>

                    <span
                      className={
                        `sv-account-chevron ${
                          expandedConnections[item.key]
                            ? 'open'
                            : ''
                        }`
                      }
                      aria-hidden="true"
                    >
                      ▾
                    </span>
                  </button>

                  {expandedConnections[item.key] && (
                    <div className="sv-account-list">
                      {item.accounts.map((account) => (
                        <div
                          className="sv-account-row"
                          key={account.key}
                        >
                          <span className="sv-account-number">
                            {account.label}
                          </span>

                          <span
                            className={
                              `sv-status ${account.tone}`
                            }
                          >
                            <i />
                            {account.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </article>
          ))}
        </div>
      </section>

      <div className="sv-row">
        <section className="sv-panel">
          <div className="sv-panel-head">
            <h2>Подключения</h2>
          </div>

          <div className="sv-stats">
            <div>
              <strong>{configured}</strong>
              <span>Настроено</span>
            </div>

            <div>
              <strong>{active}</strong>
              <span>Работают</span>
            </div>

            <div>
              <strong>{attention}</strong>
              <span>Требуют внимания</span>
            </div>
          </div>

          <div className="sv-provider-list">
            {Object.entries(
              providerCounts,
            ).map(([name, count]) => (
              <div
                className="sv-provider-row"
                key={name}
              >
                <strong>{name}</strong>
                <span>{count}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="sv-panel">
          <div className="sv-panel-head">
            <h2>Состояние системы</h2>

            <span
              className={
                `sv-status ${
                  systemOkay
                    ? 'success'
                    : 'warning'
                }`
              }
            >
              <i />
              {systemOkay
                ? 'Норма'
                : 'Проверить'}
            </span>
          </div>

          <div className="sv-system-list">
            {components.length ? (
              components.map(
                ([name, value]) => (
                  <div
                    className="sv-system-row"
                    key={name}
                  >
                    <span
                      className={
                        `sv-dot ${healthTone(value)}`
                      }
                    />

                    <strong>
                      {serviceName(name)}
                    </strong>

                    <span>
                      {healthLabel(value)}
                    </span>
                  </div>
                ),
              )
            ) : (
              <div className="sv-system-row">
                <span
                  className={
                    `sv-dot ${
                      systemOkay
                        ? 'success'
                        : 'neutral'
                    }`
                  }
                />

                <strong>
                  Основные сервисы
                </strong>

                <span>
                  {systemOkay
                    ? 'Работают'
                    : 'Нет данных'}
                </span>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
